// Simulador de Workflow Full Container
import React from "react";
import { Button, message, Popconfirm } from "antd";
import { PlayCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useWorkflowContainerFullStore } from "../../store";
import { useWidth } from "../../../../libs/hooks";

interface WorkflowSimulatorFullProps {
  onComplete?: () => void;
  disabled?: boolean;
}

export const WorkflowSimulatorFull: React.FC<WorkflowSimulatorFullProps> = ({
  onComplete,
  disabled = false,
}) => {
  const width = useWidth();
  const isMobile = width === "xs" || width === "sm";
  const [isSimulating, setIsSimulating] = React.useState(false);

  // Obtener todas las funciones del store Full
  const {
    setContainer,
    setClientId,
    setProducerId,
    setFarmId,
    setBrandId,
    setBoxId,
    setMagap,
    setZone,
    setBalerCode,
    setNumKit,
    setObservation,
    setCajasEstiba,
    setProducerShort,
    setFarmShort,
    // Campos opcionales
    setProducerOptional,
    setFarmOptional,
    setFarmOptionalZone,
    setBalerCodeOptional,
    setBrandOptional,
    setBoxOptional,
    setMagapOptional,
    setNumCajasOptional,
    setCajasEstibaOptional,
    setProducerOptionalId,
    setFarmOptionalId,
    setBoxOptionalId,
    setBrandOptionalId,
    // Horarios específicos del Full
    setCloseProcess,
    setEndLoading,
    setContainerClose,
    setContainerOulet,
    // Códigos específicos Full
    setAdhesivo,
    setAdhesivoBisagra,
    setAdhesivoPuerta,
    setBotellaExpor,
    setBotellaNaviera,
    setBotellaVerificadoraRastreo,
    setCableExpor,
    setCableVerificadora,
    setEvaluacionFruta,
    setGuiaTransporte,
    setNumCajas,
    setNumRastreoSatelital,
    setTarjaCarga,
    setTermografo,
    // Personal
    setCalidad,
    setCalidadCi,
    setGancho,
    setGanchoCi,
    setDriver,
    setPlaca,
    setCompanyTransport,
    // Funciones de fotos
    setSiglasFPhoto,
    setAdhesivoBisagraPhoto1,
    setAdhesivoBisagraPhoto2,
    setAdhesivoBisagraPhoto3,
    setAdhesivoPhoto,
    setAdhesivoPuertaPhoto,
    setBotellaExporPhoto,
    setBotellaNavieraPhoto,
    setBotellaVerificadoraRastreoPhoto,
    setCableExporPhoto,
    setCableVerificadoraPhoto,
    setEvaluacionFrutaPhoto,
    setGuiaTransportePhoto,
    setNumRastreoSatelitalPhoto,
    setTarjaCargaPhoto,
    setTarjaCargaPhotoOptional1,
    setTarjaCargaPhotoOptional2,
    setTermografoPhoto,
    setCajasConsolidadasPhoto1,
    setCajasConsolidadasPhoto2,
    setCajasConsolidadasPhoto3,
    setCajasConsolidadasPhoto4,
    setCajasConsolidadasPhoto5,
    setCierreContenedorPhoto1,
    setCierreContenedorPhoto2,
    setCierreContenedorPhoto3,
    setVentoleraDerechaPhoto,
    setVentoleraIzquierdaPhoto,
    setPanelIzquierdoPhoto,
    setPanelDerechoPhoto,
    // Videos
    setVideoCajas,
    setVideoCierreContenedor,
    setVideoPanelExterior,
  } = useWorkflowContainerFullStore();

  // Función para generar imagen placeholder y convertirla a blob URL
  const generatePlaceholderImage = (
    text: string,
    width = 400,
    height = 300
  ): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Fondo
        ctx.fillStyle = "#e6f7ff";
        ctx.fillRect(0, 0, width, height);

        // Texto principal
        ctx.fillStyle = "#1890ff";
        ctx.font = "bold 16px Arial";
        ctx.textAlign = "center";
        ctx.fillText(text, width / 2, height / 2 - 10);

        // Subtexto FULL
        ctx.fillStyle = "#52c41a";
        ctx.font = "bold 14px Arial";
        ctx.fillText("FULL CONTAINER", width / 2, height / 2 + 15);

        // Timestamp
        ctx.fillStyle = "#666";
        ctx.font = "12px Arial";
        ctx.fillText(new Date().toLocaleString(), width / 2, height / 2 + 35);
      }

      // Convertir canvas a blob y luego crear URL
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          }
        },
        "image/jpeg",
        0.8
      );
    });
  };

  // Función para generar video simulado
  const generatePlaceholderVideo = (label: string): Promise<string> => {
    return new Promise((resolve) => {
      // Crear un canvas para el video simulado
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Fondo azul
        ctx.fillStyle = "#001529";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Texto del video
        ctx.fillStyle = "white";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(
          "VIDEO SIMULADO",
          canvas.width / 2,
          canvas.height / 2 - 20
        );
        ctx.fillText(label, canvas.width / 2, canvas.height / 2 + 20);

        // Icono de play
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2 - 30, canvas.height / 2 + 50);
        ctx.lineTo(canvas.width / 2 + 30, canvas.height / 2 + 80);
        ctx.lineTo(canvas.width / 2 - 30, canvas.height / 2 + 110);
        ctx.closePath();
        ctx.fillStyle = "#52c41a";
        ctx.fill();
      }

      // Convertir a blob URL
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          }
        },
        "image/jpeg",
        0.8
      );
    });
  };

  const simulateWorkflowFull = async () => {
    try {
      setIsSimulating(true);
      message.loading("Simulando workflow FULL completo...", 0);

      // Simular demora realista
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // 1. DATOS BÁSICOS DEL FORMULARIO
      const containerNumber = `FULL${Date.now().toString().slice(-6)}`;
      setContainer(containerNumber);
      setClientId(1);
      setProducerId(1);
      setFarmId(1);
      setBrandId(1);
      setBoxId(1);
      setMagap(`MGP${Date.now().toString().slice(-4)}`);
      setZone(`ZONA-${Math.floor(Math.random() * 10) + 1}`);
      setBalerCode(`BAL${Date.now().toString().slice(-4)}`);
      setNumKit(`KITF${Date.now().toString().slice(-3)}`);
      setObservation("Datos simulados para testing - Contenedor Full");
      setCajasEstiba(Math.floor(Math.random() * 50) + 100);
      setProducerShort("PROD-SIM");
      setFarmShort("FARM-SIM");

      await new Promise((resolve) => setTimeout(resolve, 300));

      // 2. CAMPOS OPCIONALES (simulando consolidación)
      setProducerOptionalId(2);
      setFarmOptionalId(2);
      setBrandOptionalId(2);
      setBoxOptionalId(2);
      setFarmOptionalZone(`ZONA-OP-${Math.floor(Math.random() * 5) + 1}`);
      setBalerCodeOptional(`BALOP${Date.now().toString().slice(-3)}`);
      setMagapOptional(`MGPOP${Date.now().toString().slice(-3)}`);
      setNumCajasOptional(Math.floor(Math.random() * 30) + 50);
      setCajasEstibaOptional(Math.floor(Math.random() * 40) + 80);

      // 3. HORARIOS ESPECÍFICOS DEL FULL
      const now = new Date();
      const closeTime = new Date(now.getTime() - 2 * 60 * 60 * 1000);
      const endLoadingTime = new Date(now.getTime() - 1.5 * 60 * 60 * 1000);
      const containerCloseTime = new Date(now.getTime() - 1 * 60 * 60 * 1000);
      const containerOutletTime = new Date(
        now.getTime() - 0.5 * 60 * 60 * 1000
      );

      setCloseProcess(closeTime.toTimeString().slice(0, 5));
      setEndLoading(endLoadingTime.toTimeString().slice(0, 5));
      setContainerClose(containerCloseTime.toTimeString().slice(0, 5));
      setContainerOulet(containerOutletTime.toTimeString().slice(0, 5));

      await new Promise((resolve) => setTimeout(resolve, 400));

      // 4. CÓDIGOS ESPECÍFICOS DEL FULL
      setAdhesivo(`ADH${Date.now().toString().slice(-5)}`);
      setAdhesivoBisagra(`ADHB${Date.now().toString().slice(-4)}`);
      setAdhesivoPuerta(`ADHP${Date.now().toString().slice(-4)}`);
      setBotellaExpor(`BEX${Date.now().toString().slice(-5)}`);
      setBotellaNaviera(`BN${Date.now().toString().slice(-6)}`);
      setBotellaVerificadoraRastreo(`BVR${Date.now().toString().slice(-4)}`);
      setCableExpor(`CEX${Date.now().toString().slice(-5)}`);
      setCableVerificadora(`CV${Date.now().toString().slice(-6)}`);
      setEvaluacionFruta(`EF${Date.now().toString().slice(-5)}`);
      setGuiaTransporte(`GT${Date.now().toString().slice(-5)}`);
      setNumCajas(Math.floor(Math.random() * 200) + 800);
      setNumRastreoSatelital(`NRS${Date.now().toString().slice(-5)}`);
      setTarjaCarga(`TC${Date.now().toString().slice(-6)}`);
      setTermografo(`TG${Date.now().toString().slice(-6)}`);

      // 5. PERSONAL
      setCalidad("María Calidad Full");
      setCalidadCi("1234567891");
      setGancho("Pedro Gancho Full");
      setGanchoCi("1234567892");
      setDriver("Carlos Conductor Full");
      setPlaca(`FULL-${Date.now().toString().slice(-3)}`);
      setCompanyTransport("Transportes Full Container S.A.");

      await new Promise((resolve) => setTimeout(resolve, 300));
      message.destroy();
      message.loading("Generando fotos y videos FULL...", 0);

      // 6. GENERAR TODAS LAS FOTOS REQUERIDAS (27 fotos)
      const photosToGenerate = [
        {
          setter: setSiglasFPhoto,
          label: `Contenedor Full ${containerNumber}`,
        },
        { setter: setAdhesivoBisagraPhoto1, label: "Adhesivo Bisagra 1" },
        { setter: setAdhesivoBisagraPhoto2, label: "Adhesivo Bisagra 2" },
        { setter: setAdhesivoBisagraPhoto3, label: "Adhesivo Bisagra 3" },
        { setter: setAdhesivoPhoto, label: "Adhesivo Principal" },
        { setter: setAdhesivoPuertaPhoto, label: "Adhesivo Puerta" },
        { setter: setBotellaExporPhoto, label: "Botella Exportador" },
        { setter: setBotellaNavieraPhoto, label: "Botella Naviera" },
        {
          setter: setBotellaVerificadoraRastreoPhoto,
          label: "Botella Verificadora",
        },
        { setter: setCableExporPhoto, label: "Cable Exportador" },
        { setter: setCableVerificadoraPhoto, label: "Cable Verificadora" },
        { setter: setEvaluacionFrutaPhoto, label: "Evaluación Fruta" },
        { setter: setGuiaTransportePhoto, label: "Guía Transporte" },
        { setter: setNumRastreoSatelitalPhoto, label: "Rastreo Satelital" },
        { setter: setTarjaCargaPhoto, label: "Tarja Carga" },
        { setter: setTarjaCargaPhotoOptional1, label: "Tarja Opcional 1" },
        { setter: setTarjaCargaPhotoOptional2, label: "Tarja Opcional 2" },
        { setter: setTermografoPhoto, label: "Termógrafo" },
        { setter: setCajasConsolidadasPhoto1, label: "Cajas Consolidadas 1" },
        { setter: setCajasConsolidadasPhoto2, label: "Cajas Consolidadas 2" },
        { setter: setCajasConsolidadasPhoto3, label: "Cajas Consolidadas 3" },
        { setter: setCajasConsolidadasPhoto4, label: "Cajas Consolidadas 4" },
        { setter: setCajasConsolidadasPhoto5, label: "Cajas Consolidadas 5" },
        { setter: setCierreContenedorPhoto1, label: "Cierre Contenedor 1" },
        { setter: setCierreContenedorPhoto2, label: "Cierre Contenedor 2" },
        { setter: setCierreContenedorPhoto3, label: "Cierre Contenedor 3" },
        { setter: setVentoleraDerechaPhoto, label: "Ventolera Derecha" },
        { setter: setVentoleraIzquierdaPhoto, label: "Ventolera Izquierda" },
        { setter: setPanelIzquierdoPhoto, label: "Panel Izquierdo" },
        { setter: setPanelDerechoPhoto, label: "Panel Derecho" },
      ];

      // Generar y asignar cada foto
      for (let i = 0; i < photosToGenerate.length; i++) {
        const { setter, label } = photosToGenerate[i];

        try {
          // Generar la imagen como blob URL
          const imageBlobUrl = await generatePlaceholderImage(label);

          // Llamar al setter con la imagen generada
          await setter(imageBlobUrl);
          // console.log(`Foto Full generada: ${label}`);
        } catch (error) {
          console.warn(`Error generando foto ${label}:`, error);
        }

        // Pequeña pausa entre fotos
        if (i % 4 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 300));
      message.destroy();
      message.loading("Generando videos simulados...", 0);

      // 7. GENERAR VIDEOS SIMULADOS
      const videosToGenerate = [
        { setter: setVideoCajas, label: "Video Cajas" },
        { setter: setVideoCierreContenedor, label: "Video Cierre Contenedor" },
        { setter: setVideoPanelExterior, label: "Video Panel Exterior" },
      ];

      for (const { setter, label } of videosToGenerate) {
        try {
          const videoBlobUrl = await generatePlaceholderVideo(label);
          await setter(videoBlobUrl);
          // console.log(`Video Full generado: ${label}`);
        } catch (error) {
          console.warn(`Error generando video ${label}:`, error);
        }
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      message.destroy();
      message.success(
        "Workflow FULL simulado completamente! Todos los campos están llenos."
      );

      onComplete?.();
    } catch (error) {
      console.error("Error simulando workflow Full:", error);
      message.destroy();
      message.error("Error durante la simulación Full");
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Popconfirm
      title="Simular Workflow FULL Completo"
      description="¿Llenar automáticamente todos los campos con datos de prueba para contenedor lleno?"
      onConfirm={simulateWorkflowFull}
      okText="Sí, simular FULL"
      cancelText="Cancelar"
      zIndex={1601}
      icon={<PlayCircleOutlined style={{ color: "#1890ff" }} />}
    >
      {isMobile ? (
        <Button
          size="middle"
          type="default"
          icon={isSimulating ? <LoadingOutlined /> : <PlayCircleOutlined />}
          disabled={disabled || isSimulating}
          loading={isSimulating}
          style={{
            backgroundColor: "#f6ffed",
            borderColor: "#52c41a",
            color: "#52c41a",
          }}
        />
      ) : (
        <Button
          size="middle"
          type="default"
          icon={isSimulating ? <LoadingOutlined /> : <PlayCircleOutlined />}
          disabled={disabled || isSimulating}
          loading={isSimulating}
          style={{
            backgroundColor: "#f6ffed",
            borderColor: "#52c41a",
            color: "#52c41a",
          }}
        >
          {isSimulating ? "Simulando..." : "Simular"}
        </Button>
      )}
    </Popconfirm>
  );
};

export default WorkflowSimulatorFull;
